/* Noxrivet size guide — modal/inline chart, in/cm toggle and "Find my size".
   Body measurements typed by the customer stay in this page's memory: they are never stored or sent anywhere.
   Analytics events (Shopify.analytics.publish → customer-events pixels, which follow the store's consent settings)
   carry only the product, chart, result type and size label. */
(() => {
  if (customElements.get('nx-sizing-guide')) return;

  const UNIT_KEY = 'nx-size-unit';
  const EPS = 0.1; // inches of measuring tolerance at range edges
  const CM = 2.54;

  const storage = {
    get() { try { return localStorage.getItem(UNIT_KEY); } catch (e) { return null; } },
    set(v) { try { localStorage.setItem(UNIT_KEY, v); } catch (e) { /* private mode */ } },
  };

  const track = (name, data) => {
    try { window.Shopify?.analytics?.publish?.(name, data); } catch (e) { /* analytics must never break sizing */ }
  };

  /* ---------- size labels (mirrors tools/sizing_cli.py size_keys) ---------- */
  const WORDS = [['EXTRASMALL', 'XS'], ['EXTRALARGE', 'XL'], ['XXXXLARGE', '4XL'], ['XXXLARGE', 'XXXL'], ['XXLARGE', 'XXL'],
    ['XLARGE', 'XL'], ['XXSMALL', 'XXS'], ['XSMALL', 'XS'], ['SMALL', 'S'], ['MEDIUM', 'M'], ['LARGE', 'L']];
  function sizeKeys(label) {
    let s = String(label).toUpperCase().replace(/[\s\-_.]/g, '');
    const plus = s.includes('PLUS');
    s = s.replace(/\(?PLUS\)?/g, '');
    for (const [a, b] of WORDS) if (s === a) { s = b; break; }
    const keys = new Set([s]);
    const jw = s.match(/^(\d+)\/(\d+)$/);
    if (jw) keys.add(jw[2]);
    if (plus && /^[0-4]$/.test(s)) keys.add(s + 'X');
    return keys;
  }
  const firstKey = (l) => sizeKeys(l).values().next().value;
  function parseAliases(text) {
    const out = {};
    (text || '').split('\n').forEach((line) => {
      const i = line.indexOf('=');
      if (i < 0) return;
      const k = firstKey(line.slice(0, i).trim());
      out[k] = out[k] || new Set();
      line.slice(i + 1).split(',').map((x) => x.trim()).filter(Boolean).forEach((x) => out[k].add(firstKey(x)));
    });
    return out;
  }
  const labelMatchesRow = (label, row, aliases) => {
    const keys = sizeKeys(label);
    const rk = firstKey(row);
    if (keys.has(rk)) return true;
    return [...(aliases[rk] || [])].some((a) => keys.has(a));
  };

  /* ---------- numbers ---------- */
  const parseLen = (v) => {
    const m = String(v).trim().match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)$/);
    if (m) return [parseFloat(m[1]), parseFloat(m[2])];
    return /^\d+(\.\d+)?$/.test(String(v).trim()) ? [parseFloat(v), parseFloat(v)] : null;
  };
  const toIn = (n, unit) => (unit === 'cm' ? n / CM : n);
  // src 'cm' + unit 'in': a converted metric chart, so one decimal is enough (21.7, not 21.65)
  const fmt = (inches, unit, src) => {
    if (unit === 'cm') {
      const cm = Math.round(inches * CM * 2) / 2; // nearest 0.5 cm
      return String(cm % 1 ? cm.toFixed(1) : cm);
    }
    if (src === 'cm') return String(Math.round(inches * 10) / 10);
    const r = Math.round(inches * 100) / 100; // keep published precision, e.g. 7.75
    return String(r);
  };
  const fmtRange = ([lo, hi], unit, src) => (lo === hi ? fmt(lo, unit, src) : `${fmt(lo, unit, src)}–${fmt(hi, unit, src)}`);
  const tpl = (s, vars) => String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function parseGrid(text) {
    const lines = String(text || '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => l.split('|').map((c) => c.trim()));
    return { head: lines[0] || [], rows: lines.slice(1) };
  }

  /* ---------- recommendation logic ---------- */
  // inputs: { column: valueInInches }. Returns a result object; never guesses outside verified ranges.
  function recommendBody(table, inputs) {
    const { head, rows } = parseGrid(table.grid);
    const conv = (v) => parseLen(v)?.map((n) => toIn(n, table.unit));
    const per = [];
    for (const [col, v] of Object.entries(inputs)) {
      const ci = head.indexOf(col);
      const ranges = rows.map((r) => ({ size: r[0], range: conv(r[ci]) })).filter((x) => x.range);
      const fits = ranges.filter((x) => v >= x.range[0] - EPS && v <= x.range[1] + EPS).map((x) => x.size);
      const min = Math.min(...ranges.map((x) => x.range[0]));
      const max = Math.max(...ranges.map((x) => x.range[1]));
      if (fits.length) per.push({ col, v, kind: 'in', sizes: fits });
      else if (v < min) per.push({ col, v, kind: 'low', min, max });
      else if (v > max) per.push({ col, v, kind: 'high', min, max });
      else {
        const lower = [...ranges].reverse().find((x) => x.range[1] < v);
        const upper = ranges.find((x) => x.range[0] > v);
        per.push({ col, v, kind: 'between', sizes: [lower?.size, upper?.size].filter(Boolean) });
      }
    }
    const outside = per.find((p) => p.kind === 'low' || p.kind === 'high');
    if (outside) return { type: outside.kind === 'low' ? 'outside_low' : 'outside_high', per, outside };
    const order = rows.map((r) => r[0]);
    let common = order.filter((s) => per.every((p) => p.sizes.includes(s)));
    if (!common.length) return { type: 'conflict', per };
    const allIn = common.filter((s) => per.every((p) => p.kind === 'in' && p.sizes.includes(s)));
    if (allIn.length === 1) return { type: 'single', sizes: allIn, per };
    if (allIn.length > 1) return { type: 'overlap', sizes: allIn, per };
    return { type: 'between', sizes: common, per };
  }

  // Garment waist at full extension, verified adjustment below it (Dark Street): a size fits when
  // max - adjust <= waist < max; if the waist reaches a size's maximum, go up one size (supplier rule).
  function recommendAdjustable(table, inputs) {
    const { head, rows } = parseGrid(table.grid);
    const [col, v] = Object.entries(inputs)[0];
    const ci = head.indexOf(col);
    const adj = toIn(Number(table.adjust) || 0, table.unit);
    const sizes = rows.map((r) => ({ size: r[0], max: toIn(parseLen(r[ci])[1], table.unit) })).filter((x) => x.max);
    const per = [{ col, v }];
    const fits = sizes.filter((x) => v >= x.max - adj - 1e-9 && v < x.max);
    if (!fits.length) {
      const low = v < Math.min(...sizes.map((x) => x.max - adj));
      return { type: low ? 'outside_low' : 'outside_high', per,
        outside: { col, v, min: Math.min(...sizes.map((x) => x.max - adj)), max: Math.max(...sizes.map((x) => x.max)) } };
    }
    const reached = sizes.find((x) => Math.abs(x.max - v) < 1e-9);
    return { type: 'adjustable', sizes: [fits[0].size], also: fits.slice(1).map((x) => x.size), reached: reached?.size, per, adj, max: fits[0].max };
  }

  /* ---------- custom element ---------- */
  class NxSizeGuide extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-nxsz-data]');
      if (!json || this.data) return;
      try { this.data = JSON.parse(json.textContent); } catch (e) { return; }
      // `t` output is HTML-escaped by Shopify; decode once here and escape only when writing HTML.
      const decoder = document.createElement('textarea');
      this.i18n = Object.fromEntries(Object.entries(this.data.i18n).map(([k, v]) => { decoder.innerHTML = v; return [k, decoder.value]; }));
      this.dialog = this.querySelector('dialog');
      this.unit = storage.get() || this.data.defaultUnit || 'in';
      this.setupTabs();
      this.querySelectorAll('[data-nxsz-unit]').forEach((b) => b.addEventListener('click', () => this.setUnit(b.dataset.nxszUnit, true)));
      this.querySelector('[data-nxsz-close]')?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); }); // backdrop
      this.dialog?.addEventListener('close', () => this.onClosed());
      document.addEventListener('nx-size-unit', (e) => { if (e.detail !== this.unit) this.setUnit(e.detail, false); });
      this.renderCoverage();
      this.buildFinder();
      this.setUnit(this.unit, false);
      if (this.dataset.source === 'inline') this.observeInline();
    }

    /* tabs: arrow keys / Home / End, roving tabindex */
    setupTabs() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.selectTab(i));
        t.addEventListener('keydown', (e) => {
          const n = this.tabs.length;
          const to = { ArrowRight: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, Home: 0, End: n - 1 }[e.key];
          if (to === undefined) return;
          e.preventDefault();
          this.selectTab(to);
          this.tabs[to].focus();
        });
      });
    }
    selectTab(i) {
      this.tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const p = this.querySelector('#' + CSS.escape(t.getAttribute('aria-controls')));
        if (p) p.hidden = !on;
      });
    }

    /* units */
    setUnit(unit, user) {
      if (unit !== 'in' && unit !== 'cm') return;
      this.unit = unit;
      this.querySelectorAll('[data-nxsz-unit]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.nxszUnit === unit));
      this.querySelectorAll('[data-nxsz-table]').forEach((table) => {
        const src = table.dataset.unit || 'in';
        table.querySelectorAll('[data-nxsz-len]').forEach((td) => {
          const r = parseLen(td.dataset.nxszLen);
          if (r) td.textContent = fmtRange(r.map((n) => toIn(n, src)), unit, src);
        });
        table.querySelectorAll('[data-nxsz-u]').forEach((u) => { u.textContent = `(${this.i18n['unit_' + unit]})`; });
        table.querySelectorAll('[data-nxsz-unit-name]').forEach((u) => { u.textContent = this.i18n[`unit_${unit}_long`]; });
      });
      // free-text notes such as "Rise: 7.75 in" or "up to 4 in"
      this.querySelectorAll('[data-nxsz-convert-text]').forEach((el) => {
        if (!el.dataset.orig) el.dataset.orig = el.textContent;
        const src = el.dataset.unit || 'in';
        el.textContent = el.dataset.orig.replace(/(\d+(?:\.\d+)?)\s?(in|cm)\b/g, (m, n, u) => (u !== src ? m : `${fmt(toIn(parseFloat(n), src), unit)} ${this.i18n['unit_' + unit]}`));
      });
      this.querySelectorAll('[data-nxsz-input-unit]').forEach((s) => { s.textContent = this.i18n['unit_' + unit]; });
      if (user) {
        storage.set(unit);
        document.dispatchEvent(new CustomEvent('nx-size-unit', { detail: unit }));
      }
    }

    /* which of the product's sizes have no chart row */
    sizeRows() {
      const rows = new Set(); const aliases = {};
      this.data.tables.forEach((t) => {
        parseGrid(t.grid).rows.forEach((r) => rows.add(r[0]));
        Object.entries(parseAliases(t.aliases)).forEach(([k, v]) => { aliases[k] = new Set([...(aliases[k] || []), ...v]); });
      });
      return { rows: [...rows], aliases };
    }
    productSizes() {
      const i = this.data.sizeIndex;
      if (i === null || i === undefined) return [];
      return [...new Set(this.data.variants.map((v) => v.options[i]))];
    }
    renderCoverage() {
      const { rows, aliases } = this.sizeRows();
      const missing = this.productSizes().filter((s) => !rows.some((r) => labelMatchesRow(s, r, aliases)));
      this.querySelectorAll('[data-nxsz-coverage]').forEach((p) => {
        if (!missing.length) return;
        p.textContent = tpl(this.i18n.coverage, { sizes: missing.join(', ') });
        p.hidden = false;
      });
    }
    highlightSelected() {
      const label = this.currentSelection()?.[this.data.sizeIndex];
      const { aliases } = this.sizeRows();
      this.querySelectorAll('[data-nxsz-row]').forEach((tr) => {
        const on = !!label && labelMatchesRow(label, tr.dataset.nxszRow, aliases);
        tr.classList.toggle('is-current', on);
        if (on) tr.setAttribute('aria-current', 'true'); else tr.removeAttribute('aria-current');
      });
    }

    /* the variant picker of this product section */
    picker() {
      return document.getElementById(`variant-selects-${this.dataset.sectionId}`);
    }
    // Dawn's captured input names can carry whitespace ("Size-1\n"), so compare trimmed names.
    radios(vs, i) {
      const want = `${this.data.optionNames[i]}-${i + 1}`;
      return [...vs.querySelectorAll('input[type="radio"]')].filter((r) => r.name.trim() === want);
    }
    selectFor(vs, i) {
      const want = `options[${this.data.optionNames[i]}]`;
      return [...vs.querySelectorAll('select')].find((s) => s.name.trim() === want) || null;
    }
    currentSelection() {
      const vs = this.picker();
      if (!vs) return null;
      return this.data.optionNames.map((name, i) => {
        const sel = this.selectFor(vs, i);
        if (sel) return sel.value;
        return this.radios(vs, i).find((r) => r.checked)?.value ?? null;
      });
    }

    /* ---------- finder ---------- */
    finderTable() {
      return this.data.tables.find((t) => t.mode === 'body_range' || t.mode === 'adjustable_max');
    }
    buildFinder() {
      const form = this.querySelector('[data-nxsz-finder]');
      const t = this.finderTable();
      if (!form || !t) return;
      this.finder = { form, table: t, result: this.querySelector('[data-nxsz-result]'), error: form.querySelector('[data-nxsz-error]') };
      const adjustable = t.mode === 'adjustable_max';
      form.querySelector('[data-nxsz-finder-intro]').textContent = adjustable ? this.i18n.intro_adjustable : this.i18n.intro_body;
      const how = {};
      String(this.data.howTo || '').split('\n').forEach((l) => {
        const i = l.indexOf(':');
        if (i > 0) how[l.slice(0, i).trim().toLowerCase()] = l.slice(i + 1).trim();
      });
      const hintFor = (col) => {
        const k = Object.keys(how).find((h) => h.split(/\s*\/\s*/).some((part) => col.toLowerCase().startsWith(part) || part.startsWith(col.toLowerCase())));
        return k ? how[k] : '';
      };
      const fields = form.querySelector('[data-nxsz-fields]');
      const uid = this.id || this.dialog?.id || 'nxsg';
      (t.inputs || []).forEach((col, i) => {
        const id = `${uid}-in-${i}`;
        const label = adjustable ? this.i18n.input_adjustable : col.replace(/\s*\(.*\)$/, '');
        const hint = adjustable ? '' : hintFor(col);
        const optional = !adjustable && t.inputs.length > 1;
        fields.insertAdjacentHTML('beforeend', `
          <div class="nxsz__field">
            <label for="${id}">${esc(label)}${optional ? ` <span class="nxsz__opt">${esc(this.i18n.optional)}</span>` : ''}</label>
            ${hint ? `<p class="nxsz__hint" id="${id}-hint">${esc(hint)}</p>` : ''}
            <div class="nxsz__input">
              <input id="${id}" name="${esc(col)}" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" ${hint ? `aria-describedby="${id}-hint"` : ''}>
              <span data-nxsz-input-unit aria-hidden="true">${esc(this.i18n['unit_' + this.unit])}</span>
            </div>
          </div>`);
      });
      form.addEventListener('submit', (e) => { e.preventDefault(); this.runFinder(); });
    }
    runFinder() {
      const { form, table, result, error } = this.finder;
      const inputs = {};
      let bad = null;
      form.querySelectorAll('input').forEach((inp) => {
        inp.removeAttribute('aria-invalid');
        const raw = inp.value.trim().replace(',', '.');
        if (!raw) return;
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0 || n > (this.unit === 'cm' ? 250 : 100)) { bad = bad || inp; inp.setAttribute('aria-invalid', 'true'); return; }
        inputs[inp.name] = toIn(n, this.unit);
      });
      error.hidden = true;
      if (bad) { error.textContent = this.i18n.err_invalid; error.hidden = false; bad.focus(); return; }
      if (!Object.keys(inputs).length) { error.textContent = this.i18n.err_none; error.hidden = false; form.querySelector('input')?.focus(); return; }
      const res = table.mode === 'adjustable_max' ? recommendAdjustable(table, inputs) : recommendBody(table, inputs);
      this.renderResult(res, table);
      track('nx_size_recommendation_shown', { product_id: this.data.productId, chart: this.data.chart, result: res.type, sizes: (res.sizes || []).join(',') });
    }
    v(inches) { return `${fmt(inches, this.unit)} ${this.i18n['unit_' + this.unit]}`; }
    renderResult(res, table) {
      const I = this.i18n; const out = [];
      const name = (c) => c.replace(/\s*\(.*\)$/, '');
      const col = (c) => name(c).toLowerCase();
      const why = (res.per || []).map((p) => {
        if (p.kind === 'in') return tpl(I.why_in, { measurement: name(p.col), value: this.v(p.v), sizes: p.sizes.join(' / ') });
        if (p.kind === 'between') return tpl(I.why_between, { measurement: name(p.col), value: this.v(p.v), sizes: p.sizes.join(' / ') });
        return '';
      }).filter(Boolean);
      let head = '';
      switch (res.type) {
        case 'single': head = tpl(I.res_single, { size: res.sizes[0] }); break;
        case 'overlap': head = tpl(I.res_overlap, { sizes: res.sizes.join(' / ') }); out.push(I.res_between_tip); break;
        case 'between': head = tpl(I.res_between, { sizes: res.sizes.join(' / ') }); out.push(I.res_between_tip); break;
        case 'adjustable':
          head = tpl(I.res_adjustable, { size: res.sizes[0] });
          why.push(tpl(I.why_adjustable, { value: this.v(res.per[0].v), size: res.sizes[0], max: this.v(res.max), adjust: this.v(res.adj) }));
          if (res.reached) out.push(tpl(I.res_adjustable_up, { size: res.reached, next: res.sizes[0] }));
          if (res.also.length) out.push(tpl(I.res_adjustable_more, { sizes: res.also.join(' / ') }));
          break;
        case 'conflict':
          head = I.res_conflict;
          res.per.forEach((p) => why.push(tpl(p.kind === 'in' ? I.why_in : I.why_between, { measurement: name(p.col), value: this.v(p.v), sizes: p.sizes.join(' / ') })));
          break;
        default: {
          const o = res.outside;
          head = tpl(res.type === 'outside_low' ? I.res_outside_low : I.res_outside_high, { measurement: col(o.col), value: this.v(o.v), min: this.v(o.min), max: this.v(o.max) });
        }
      }
      const actionable = ['single', 'overlap', 'between', 'adjustable'].includes(res.type);
      const buttons = actionable ? this.sizeActions(res.sizes, table) : '';
      const html = `
        <p class="nxsz__badge">${esc(I.guidance)}</p>
        <p class="nxsz__rec">${esc(head)}</p>
        ${why.length ? `<ul class="nxsz__why" role="list">${[...new Set(why)].map((w) => `<li>${esc(w)}</li>`).join('')}</ul>` : ''}
        ${out.map((o) => `<p class="nxsz__note">${esc(o)}</p>`).join('')}
        ${buttons}
        ${actionable ? '' : `<p class="nxsz__help">${esc(I.help)}</p>`}`;
      this.finder.result.innerHTML = html;
      this.finder.result.querySelectorAll('[data-nxsz-select]').forEach((b) => b.addEventListener('click', () => this.selectSize(b.dataset.nxszSelect)));
      this.finder.result.focus({ preventScroll: false });
    }
    /* map chart rows → real variant labels, keeping the current colour / other options */
    sizeActions(rowSizes, table) {
      const I = this.i18n; const idx = this.data.sizeIndex;
      if (idx === null || idx === undefined) return '';
      const aliases = parseAliases(table.aliases);
      const sel = this.currentSelection() || [];
      const others = this.data.optionNames.map((n, i) => (i === idx ? null : sel[i])).filter(Boolean);
      const otherLabel = others.join(' / ');
      const parts = rowSizes.map((row) => {
        const labels = this.productSizes().filter((l) => labelMatchesRow(l, row, aliases));
        if (!labels.length) return `<li>${esc(tpl(I.not_in_option, { size: row }))}</li>`;
        return labels.map((label) => {
          const variant = this.data.variants.find((v) => v.options[idx] === label && v.options.every((o, i) => i === idx || sel[i] == null || o === sel[i]));
          if (!variant) return `<li>${esc(tpl(I.not_offered, { size: label, options: otherLabel }))}</li>`;
          if (!variant.available) return `<li><button type="button" class="nx-btn nxsz__select" disabled>${esc(tpl(I.sold_out, { size: label, options: otherLabel }))}</button></li>`;
          return `<li><button type="button" class="nx-btn nxsz__select" data-nxsz-select="${esc(label)}">${esc(tpl(I.select, { size: label }))}</button></li>`;
        }).join('');
      });
      return `<ul class="nxsz__actions" role="list">${parts.join('')}</ul>`;
    }
    selectSize(label) {
      const vs = this.picker(); const idx = this.data.sizeIndex;
      if (!vs) return;
      const sel = this.selectFor(vs, idx);
      let target = null;
      if (sel) {
        sel.value = label;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        target = sel;
      } else {
        const radio = this.radios(vs, idx).find((r) => r.value === label);
        if (!radio) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        target = radio;
      }
      track('nx_size_recommendation_selected', { product_id: this.data.productId, chart: this.data.chart, size: label });
      this.returnFocusTo = target.id;
      if (this.dialog?.open) this.close();
      else this.focusPicker(target.id);
    }
    focusPicker(id) {
      // the theme re-renders the picker after a change; focus the fresh input once it lands
      const tryFocus = (n) => {
        const el = document.getElementById(id);
        if (el && el.isConnected) el.focus({ preventScroll: true });
        else if (n > 0) setTimeout(() => tryFocus(n - 1), 150);
      };
      setTimeout(() => tryFocus(10), 50);
    }

    /* ---------- dialog ---------- */
    open(opener) {
      if (!this.dialog) return;
      this.opener = opener;
      this.highlightSelected();
      this.dialog.showModal();
      document.documentElement.classList.add('nxsz-lock');
      this.querySelector('[role="tab"][aria-selected="true"]')?.focus();
      track('nx_size_guide_opened', { product_id: this.data.productId, chart: this.data.chart, source: 'button' });
    }
    close() { this.dialog?.close(); }
    onClosed() {
      document.documentElement.classList.remove('nxsz-lock');
      if (this.returnFocusTo) { this.focusPicker(this.returnFocusTo); this.returnFocusTo = null; return; }
      const sid = this.dialog.id;
      const opener = this.opener?.isConnected ? this.opener : document.querySelector(`[data-nxsz-open="${CSS.escape(sid)}"]`);
      opener?.focus();
    }
    observeInline() {
      this.highlightSelected();
      const vs = this.picker();
      vs?.closest('section, .shopify-section')?.addEventListener('change', () => setTimeout(() => this.highlightSelected(), 400));
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          track('nx_size_guide_opened', { product_id: this.data.productId, chart: this.data.chart, source: 'inline' });
          io.disconnect();
        }
      }, { threshold: 0.4 });
      io.observe(this);
    }
  }
  customElements.define('nx-sizing-guide', NxSizeGuide);

  // Trigger buttons live inside the variant picker, which the theme re-renders on every option change,
  // so they are handled by delegation instead of per-button listeners.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-nxsz-open]');
    if (!btn) return;
    const dialog = document.getElementById(btn.dataset.nxszOpen);
    dialog?.closest('nx-sizing-guide')?.open(btn);
  });

  // exposed for automated boundary tests only
  window.NxSizeGuideLogic = { recommendBody, recommendAdjustable, sizeKeys, parseAliases, labelMatchesRow, fmt };
})();
